import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, Search, Loader2, Inbox, Filter as FilterIcon, ChevronRight, Layers, Clock } from "lucide-react";
import api, { fmtINR, shortDate, BACKEND_URL, downloadBlob } from "@/lib/api";

// Session-gap threshold for the "smart grouping" view. Two consecutive
// txns from the same customer + link are considered the same checkout
// session if their gap is BELOW this threshold (30 minutes by default).
// Picked to match the real-world retry behaviour we see: customer
// fumbles 1-2 failed UPI intents, then nails it on the 3rd within a
// 10-15 min window. After ~30 min they've usually walked away.
const SESSION_GAP_MS = 30 * 60 * 1000;

// localStorage key for the user's grouping preference. Stored as
// 'on'|'off' so the value is human-debuggable in DevTools.
const GROUPING_KEY = "tx_grouping_on";

const STATUS_LABELS = {
    "": "All statuses",
    pending: "Pending",
    submitted: "Submitted",
    success: "Success",
    failed: "Failed",
};

const SORT_OPTIONS = [
    { value: "newest", label: "Newest first" },
    { value: "oldest", label: "Oldest first" },
    { value: "amount_desc", label: "Highest amount" },
    { value: "amount_asc", label: "Lowest amount" },
    { value: "username", label: "Username A-Z" },
];

export default function AdminTransactions() {
    const location = useLocation();
    const isPartner = location.pathname.startsWith("/partner");
    const baseUrl = isPartner ? "/partner/transactions" : "/admin/transactions";
    const exportUrl = isPartner ? "/partner/transactions/export" : "/admin/transactions/export";
    const summaryUrl = isPartner ? "/partner/transactions/summary-by-link" : "/admin/transactions/summary-by-link";
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState([]);
    const [status, setStatus] = useState("");
    const [q, setQ] = useState("");
    const [linkFilter, setLinkFilter] = useState("");
    const [sort, setSort] = useState("newest");
    const [busy, setBusy] = useState(false);
    const [firstLoaded, setFirstLoaded] = useState(false);
    const [linksList, setLinksList] = useState([]);
    // iter-60: mandatory date-range filter with sensible default so we
    // never send a bare `/transactions?` that scans a partner's full
    // history and 504s. "today" is the most-used agent workflow;
    // "custom" reveals the from/to inputs.
    const [datePreset, setDatePreset] = useState("today");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    // Resolve the current preset to an effective (from, to) UTC-ISO pair.
    // Uses IST-day boundaries because this is an Indian payment gateway
    // and "today" needs to mean today in IST regardless of the browser TZ.
    const resolveDateRange = () => {
        if (datePreset === "all") return { from: "", to: "" };
        if (datePreset === "custom") return { from: dateFrom, to: dateTo };
        const nowIst = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
        const startOfDay = (d) => { const s = new Date(d); s.setHours(0, 0, 0, 0); return s; };
        const endOfDay = (d) => { const e = new Date(d); e.setHours(23, 59, 59, 999); return e; };
        let from;
        let to = endOfDay(nowIst);
        if (datePreset === "today") from = startOfDay(nowIst);
        else if (datePreset === "yesterday") {
            const y = new Date(nowIst); y.setDate(y.getDate() - 1);
            from = startOfDay(y); to = endOfDay(y);
        } else if (datePreset === "7d") {
            const s = new Date(nowIst); s.setDate(s.getDate() - 6); from = startOfDay(s);
        } else if (datePreset === "30d") {
            const s = new Date(nowIst); s.setDate(s.getDate() - 29); from = startOfDay(s);
        } else return { from: "", to: "" };
        return { from: from.toISOString(), to: to.toISOString() };
    };

    const load = async () => {
        setBusy(true);
        const params = new URLSearchParams();
        if (status) params.set("status", status);
        if (q) params.set("q", q);
        if (linkFilter) params.set("link_code", linkFilter);
        // Server understands "oldest" / default newest — for the rest we sort
        // client-side over the returned page, which is fast for ≤200 rows.
        if (sort === "oldest") params.set("sort", "oldest");
        // iter-60: ALWAYS send a date range unless the caller explicitly picks
        // "All time". Bounded query = bounded scan even if the backend hasn't
        // been redeployed with iter-54's server-side default.
        const { from, to } = resolveDateRange();
        if (from) params.set("date_from", from);
        if (to) params.set("date_to", to);
        try {
            const r = await api.get(`${baseUrl}?${params.toString()}`, { timeout: 25000 });
            setItems(r.data.items || []);
        } catch (e) {
            // Surface a helpful message instead of a raw 504 in the console.
            // Keep the row list clean — child rendering handles empty state.
            setItems([]);
            const s = e?.response?.status;
            if (s === 504 || e?.code === "ECONNABORTED") {
                console.warn("[admin-txns] load timed out — try Today / Yesterday, or wait for the current deploy to finish.");
            } else {
                console.warn("[admin-txns] load failed:", s, e?.message);
            }
        } finally {
            setBusy(false);
            setFirstLoaded(true);
        }
    };

    const loadSummary = async () => {
        try {
            const params = new URLSearchParams({ status: "success" });
            const r = await api.get(`${summaryUrl}?${params.toString()}`);
            setSummary(r.data.items || []);
        } catch (e) {
            // Non-fatal: summary powers a secondary rollup panel. Log so an
            // ops person tailing DevTools can spot a systemic failure but
            // don't surface to the user — main txn list still renders.
            console.warn("[admin-txns] summary fetch failed:", e?.response?.status, e?.message);
        }
    };

    const loadLinks = async () => {
        try {
            if (isPartner) {
                const r = await api.get("/partner/payment-links");
                setLinksList(r.data.items || []);
            } else {
                const r = await api.get(summaryUrl); // returns all link_codes ever seen
                const seen = new Map();
                for (const it of (r.data.items || [])) {
                    if (it.link_code && !seen.has(it.link_code)) {
                        seen.set(it.link_code, { link_code: it.link_code, display_name: it.display_name });
                    }
                }
                setLinksList([...seen.values()]);
            }
        } catch (e) {
            // Non-fatal: link-filter dropdown gracefully falls back to
            // "all links" if this fails. Log for debugging.
            console.warn("[admin-txns] links fetch failed:", e?.response?.status, e?.message);
        }
    };

    useEffect(() => { load(); loadSummary();   }, [status, isPartner, linkFilter, sort, datePreset, dateFrom, dateTo]);
    useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t);   }, [q]);
    useEffect(() => { loadLinks();   }, [isPartner]);

    const sortedItems = useMemo(() => {
        const arr = [...items];
        switch (sort) {
            case "amount_desc": arr.sort((a, b) => (b.amount || 0) - (a.amount || 0)); break;
            case "amount_asc":  arr.sort((a, b) => (a.amount || 0) - (b.amount || 0)); break;
            case "username":    arr.sort((a, b) => (a.customer_username || "").localeCompare(b.customer_username || "")); break;
            // newest/oldest → already sorted by server
        }
        return arr;
    }, [items, sort]);

    // ── Smart grouping state ────────────────────────────────────────
    // The user wanted: when a customer makes multiple attempts within
    // a short window and one succeeds, show ONLY the success at the
    // top level with the failed attempts tucked under an expand chip.
    // Three successes in a row stay as three rows. Implementation is
    // pure-frontend (no API change) so the export CSV still mirrors
    // the raw DB view.
    const [groupingOn, setGroupingOn] = useState(() => {
        // Default ON (user picked 4c: "remember last choice per user").
        // First-time visitors see grouping enabled — typically what they
        // want when the queue gets noisy.
        try {
            const v = localStorage.getItem(GROUPING_KEY);
            return v !== "off"; // "on" or null → on
        } catch { return true; }
    });
    const toggleGrouping = (v) => {
        setGroupingOn(v);
        try { localStorage.setItem(GROUPING_KEY, v ? "on" : "off"); } catch (e) { console.warn("ls write:", e); }
    };
    const [expandedGroups, setExpandedGroups] = useState(() => new Set());
    const toggleExpand = (txnId) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(txnId)) next.delete(txnId); else next.add(txnId);
            return next;
        });
    };

    // Build the grouped view. Output shape: an array of
    //   { primary: txn, attempts: [txn, ...] }
    // where `attempts` is the list of failed/expired sibling txns that
    // attach to this success (newest attempt first inside the group).
    // Standalone txns (no siblings) come out as { primary, attempts: [] }.
    //
    // Algorithm:
    //   1. Bucket txns by (customer_username + link_code). Two txns
    //      from different customers or different merchants never group.
    //   2. Within each bucket, sort by created_at ASC.
    //   3. Walk through. Maintain a list of "unclaimed" failures.
    //      When a success is encountered, it CLAIMS every unclaimed
    //      failure whose gap to the previous txn (or session start) is
    //      < SESSION_GAP_MS. Failures outside that window remain
    //      standalone.
    //   4. At the end of each bucket, any leftover unclaimed failures
    //      emit as standalone rows.
    //   5. Finally, sort the emitted groups using the user's `sort`
    //      preference so the UX matches the flat-view sort order.
    const groupedItems = useMemo(() => {
        if (!groupingOn) {
            // Grouping off — every txn is its own group.
            return sortedItems.map((t) => ({ primary: t, attempts: [] }));
        }
        // Bucket by customer + link. Use empty-string fallbacks so
        // unlinked txns still group correctly with each other.
        const buckets = new Map();
        for (const t of items) {
            const key = `${t.customer_username || ""}::${t.link_code || ""}`;
            if (!buckets.has(key)) buckets.set(key, []);
            buckets.get(key).push(t);
        }
        const groups = [];
        for (const bucket of buckets.values()) {
            bucket.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            let unclaimed = []; // failed/expired txns waiting for a later success
            for (let i = 0; i < bucket.length; i++) {
                const t = bucket[i];
                const isSuccess = t.status === "success";
                if (i > 0) {
                    const gap = new Date(t.created_at) - new Date(bucket[i - 1].created_at);
                    if (gap > SESSION_GAP_MS) {
                        // Session boundary — flush leftover failures
                        // as standalone rows (user explicitly wanted
                        // "all-failed" sessions to render individually).
                        for (const u of unclaimed) groups.push({ primary: u, attempts: [] });
                        unclaimed = [];
                    }
                }
                if (isSuccess) {
                    // Success claims the failures that came before it
                    // in this session. Newest attempt first so the
                    // expanded list reads "most recent → oldest".
                    groups.push({ primary: t, attempts: [...unclaimed].reverse() });
                    unclaimed = [];
                } else {
                    unclaimed.push(t);
                }
            }
            // End of bucket: flush any remaining unclaimed (all-failed session)
            for (const u of unclaimed) groups.push({ primary: u, attempts: [] });
        }
        // Re-sort according to the user's sort preference (using the
        // primary txn for the comparator).
        const cmp = (() => {
            switch (sort) {
                case "amount_desc": return (a, b) => (b.primary.amount || 0) - (a.primary.amount || 0);
                case "amount_asc":  return (a, b) => (a.primary.amount || 0) - (b.primary.amount || 0);
                case "username":    return (a, b) => (a.primary.customer_username || "").localeCompare(b.primary.customer_username || "");
                case "oldest":      return (a, b) => new Date(a.primary.created_at) - new Date(b.primary.created_at);
                case "newest":
                default:            return (a, b) => new Date(b.primary.created_at) - new Date(a.primary.created_at);
            }
        })();
        groups.sort(cmp);
        return groups;
    }, [items, sortedItems, groupingOn, sort]);

    const exportCsv = async () => {
        const params = new URLSearchParams();
        if (status) params.set("status", status);
        const res = await fetch(`${BACKEND_URL}/api${exportUrl}?${params.toString()}`, { credentials: "include" });
        downloadBlob(await res.blob(), `transactions-${Date.now()}.csv`);
    };

    return (
        <div className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Transactions</h1>
                    <p className="text-sm text-slate-500 mt-1">All payments {isPartner ? "for your account" : "across partners"}.</p>
                </div>
                <div className="flex items-center gap-2">
                    {/* Smart Grouping toggle — collapses retry-storms into
                        a single success row with the failed attempts under
                        an expand chip. Preference is persisted to
                        localStorage so power agents keep their choice. */}
                    <button data-testid="tx-grouping-toggle"
                            onClick={() => toggleGrouping(!groupingOn)}
                            title={groupingOn ? "Smart grouping ON — click to flatten" : "Smart grouping OFF — click to group retries"}
                            className={`px-3 py-2 rounded-md text-sm flex items-center gap-2 transition-colors border ${
                                groupingOn
                                    ? "bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                                    : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                            }`}>
                        <Layers className="w-4 h-4" /> {groupingOn ? "Grouped" : "Flat view"}
                    </button>
                    <button data-testid="tx-export-btn" onClick={exportCsv} className="btn-secondary px-3 py-2 rounded-md text-sm flex items-center gap-2"><Download className="w-4 h-4" /> Export CSV</button>
                </div>
            </div>

            {summary.length > 0 && (
                <SummaryCards
                    summary={summary}
                    activeLink={linkFilter}
                    onPick={(code) => setLinkFilter(linkFilter === code ? "" : code)}
                    accent="emerald"
                    label="Successful"
                />
            )}

            <div className="card p-3 sm:p-4 flex flex-wrap items-end gap-3" data-testid="tx-filters">
                <div className="flex-1 min-w-[220px]">
                    <label className="label">Universal search</label>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input data-testid="tx-search" value={q} onChange={(e) => setQ(e.target.value)}
                                className="input pl-9" placeholder="Username, UTR, txn id, or link…" />
                    </div>
                </div>
                <div>
                    <label className="label">Status</label>
                    <select data-testid="tx-status" value={status} onChange={(e) => setStatus(e.target.value)} className="input w-40">
                        {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                </div>
                <div>
                    <label className="label">Date</label>
                    <select data-testid="tx-date-preset" value={datePreset}
                            onChange={(e) => setDatePreset(e.target.value)}
                            className="input w-36">
                        <option value="today">Today</option>
                        <option value="yesterday">Yesterday</option>
                        <option value="7d">Last 7 days</option>
                        <option value="30d">Last 30 days</option>
                        <option value="all">All time</option>
                        <option value="custom">Custom range…</option>
                    </select>
                </div>
                {datePreset === "custom" && (
                    <>
                        <div>
                            <label className="label">From</label>
                            <input data-testid="tx-date-from" type="date" value={dateFrom}
                                   onChange={(e) => setDateFrom(e.target.value ? new Date(e.target.value).toISOString() : "")}
                                   className="input" />
                        </div>
                        <div>
                            <label className="label">To</label>
                            <input data-testid="tx-date-to" type="date" value={dateTo}
                                   onChange={(e) => setDateTo(e.target.value ? new Date(e.target.value + "T23:59:59").toISOString() : "")}
                                   className="input" />
                        </div>
                    </>
                )}
                <div>
                    <label className="label">Website / Link</label>
                    <select data-testid="tx-link" value={linkFilter} onChange={(e) => setLinkFilter(e.target.value)} className="input w-48">
                        <option value="">All websites</option>
                        {linksList.map((l) => <option key={l.link_code} value={l.link_code}>{l.display_name || l.link_code}</option>)}
                    </select>
                </div>
                <div>
                    <label className="label">Sort by</label>
                    <select data-testid="tx-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="input w-44">
                        {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
                {linkFilter && (
                    <button data-testid="tx-clear-link" onClick={() => setLinkFilter("")}
                            className="text-xs text-blue-700 hover:underline px-2 py-2 flex items-center gap-1">
                        <FilterIcon className="w-3 h-3" /> Clear link filter
                    </button>
                )}
            </div>

            <div className="card overflow-hidden">
                {/* min-height keeps the card stable while data is reloading */}
                <div className="min-h-[260px] relative">
                    {busy && firstLoaded && (
                        <div className="absolute top-0 right-0 m-3 z-10 inline-flex items-center gap-1.5 text-[11px] text-slate-500 bg-white/80 backdrop-blur-sm px-2 py-1 rounded-full border border-slate-200">
                            <Loader2 className="w-3 h-3 animate-spin" /> updating
                        </div>
                    )}
                    {!firstLoaded ? (
                        <div className="px-4 py-16 text-center text-slate-400 text-sm">
                            <Loader2 className="w-5 h-5 mx-auto animate-spin opacity-60" />
                            <div className="mt-2">Loading transactions…</div>
                        </div>
                    ) : groupedItems.length === 0 ? (
                        <div className="px-4 py-16 text-center text-slate-400 text-sm" data-testid="tx-empty">
                            <Inbox className="w-6 h-6 mx-auto opacity-40" />
                            <div className="mt-2">No transactions match.</div>
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="px-4 py-2.5 text-left">Txn</th>
                                    <th className="px-4 py-2.5 text-left">Username</th>
                                    <th className="px-4 py-2.5 text-left">Method</th>
                                    <th className="px-4 py-2.5 text-left">UTR</th>
                                    <th className="px-4 py-2.5 text-right">Amount</th>
                                    <th className="px-4 py-2.5">Status</th>
                                    <th className="px-4 py-2.5 text-left">Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groupedItems.map((g) => (
                                    <TxnGroupRows key={g.primary.txn_id}
                                                  group={g}
                                                  expanded={expandedGroups.has(g.primary.txn_id)}
                                                  onToggle={() => toggleExpand(g.primary.txn_id)} />
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

/** Renders a primary txn row plus the expandable list of failed
 *  prior-attempt sibling rows. Lives in this file (not extracted) so
 *  the parent's expandedGroups Set + toggleExpand callback flow remains
 *  local — the parent owns the open/closed state. */
function TxnGroupRows({ group, expanded, onToggle }) {
    const { primary, attempts } = group;
    const hasAttempts = attempts.length > 0;
    // Time-to-success: gap between the EARLIEST failed attempt and the
    // success. Small qualitative win for partners optimising conversion.
    const ttsMs = hasAttempts
        ? new Date(primary.created_at) - new Date(attempts[attempts.length - 1].created_at)
        : 0;
    const ttsLabel = ttsMs > 0 ? fmtElapsed(ttsMs) : null;
    return (
        <>
            <tr data-testid={`tx-row-${primary.txn_id}`}
                className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                <td className="px-4 py-3 font-mono text-xs">
                    <div className="flex items-center gap-1.5">
                        {hasAttempts && (
                            <button
                                data-testid={`tx-expand-${primary.txn_id}`}
                                type="button"
                                onClick={onToggle}
                                title={expanded ? "Hide prior attempts" : "Show prior attempts"}
                                className="inline-flex items-center justify-center w-5 h-5 rounded text-slate-500 hover:bg-slate-200 transition-colors"
                            >
                                <ChevronRight className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-90" : ""}`} />
                            </button>
                        )}
                        <span>{primary.txn_id.slice(-10)}</span>
                    </div>
                </td>
                <td className="px-4 py-3 font-medium">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <span>{primary.customer_username}</span>
                        {/* At-a-glance attempts chip — clickable, identical effect to the chevron */}
                        {hasAttempts && (
                            <button
                                type="button"
                                onClick={onToggle}
                                data-testid={`tx-attempts-chip-${primary.txn_id}`}
                                title="Click to view the prior failed attempts in this session"
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 transition-colors"
                            >
                                <Layers className="w-2.5 h-2.5" /> +{attempts.length} prior {attempts.length === 1 ? "attempt" : "attempts"}
                            </button>
                        )}
                        {ttsLabel && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  title="Time from first failed attempt to this success">
                                <Clock className="w-2.5 h-2.5" /> {ttsLabel} to convert
                            </span>
                        )}
                    </div>
                </td>
                <td className="px-4 py-3"><span className="pill pill-info">{primary.channel_type}</span> <span className="text-xs text-slate-500">{(primary.channel_snapshot || {}).label}</span></td>
                <td className="px-4 py-3 font-mono text-xs">{primary.utr || "—"}</td>
                <td className="px-4 py-3 text-right font-mono">{fmtINR(primary.amount)}</td>
                <td className="px-4 py-3 text-center"><span className={`pill ${primary.status === "success" ? "pill-success" : primary.status === "submitted" || primary.status === "pending" ? "pill-pending" : "pill-failed"}`}>{primary.status}</span></td>
                <td className="px-4 py-3 text-xs text-slate-500">{shortDate(primary.created_at)}</td>
            </tr>
            {expanded && attempts.map((a) => (
                <tr key={a.txn_id}
                    data-testid={`tx-attempt-${a.txn_id}`}
                    className="border-b border-slate-100 last:border-0 bg-slate-50/40 text-slate-500">
                    <td className="px-4 py-2 pl-12 font-mono text-[11px]">
                        <span className="inline-block w-3 border-l border-b border-slate-300 h-3 mr-1.5 align-middle"></span>
                        {a.txn_id.slice(-10)}
                    </td>
                    <td className="px-4 py-2 text-xs italic">prior attempt</td>
                    <td className="px-4 py-2"><span className="pill pill-info opacity-60">{a.channel_type}</span></td>
                    <td className="px-4 py-2 font-mono text-[11px]">{a.utr || "—"}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">{fmtINR(a.amount)}</td>
                    <td className="px-4 py-2 text-center"><span className={`pill ${a.status === "submitted" || a.status === "pending" ? "pill-pending" : "pill-failed"} opacity-80`}>{a.status}</span></td>
                    <td className="px-4 py-2 text-[11px] text-slate-400">{shortDate(a.created_at)}</td>
                </tr>
            ))}
        </>
    );
}

// Format a duration in ms into a human-readable "12 min", "3h 20m" etc.
// Used only in TxnGroupRows so kept module-private.
function fmtElapsed(ms) {
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m} min`;
    const h = Math.floor(m / 60);
    const rem = m % 60;
    return rem ? `${h}h ${rem}m` : `${h}h`;
}

/** Shared summary-cards row — used by Transactions (success) and Approvals
 *  (submitted). Each card shows total count + INR value, with a sub-line
 *  splitting into FTD vs repeat. Click toggles the link_code filter. */
export function SummaryCards({ summary, activeLink, onPick, accent = "emerald", label = "Total" }) {
    const accentMap = {
        emerald: "border-emerald-300 bg-emerald-50/50 ring-emerald-300",
        amber: "border-amber-300 bg-amber-50/40 ring-amber-300",
    };
    const baseAccent = accent === "amber" ? "text-amber-700" : "text-emerald-700";
    return (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="tx-summary-cards">
            {summary.slice(0, 12).map((s) => {
                const active = activeLink === s.link_code;
                return (
                    <button key={`${s.partner_id || ""}-${s.link_code}`}
                            data-testid={`summary-card-${s.link_code}`}
                            onClick={() => onPick(s.link_code)}
                            className={`text-left p-3 rounded-md border-2 transition-all hover:shadow-sm ${
                                active
                                    ? `${accentMap[accent] || accentMap.emerald} ring-2`
                                    : "border-slate-200 bg-white hover:border-slate-300"
                            }`}>
                        <div className="flex items-baseline justify-between gap-2">
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 truncate" title={s.display_name}>{s.display_name}</div>
                            <div className={`text-[10px] uppercase font-bold ${baseAccent}`}>{label}</div>
                        </div>
                        <div className="font-mono text-lg font-bold text-slate-900 mt-1.5 leading-none">{fmtINR(s.value)}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{s.count.toLocaleString("en-IN")} txns</div>
                        <div className="border-t border-slate-100 mt-2 pt-1.5 flex items-center justify-between gap-2 text-[10px]">
                            <div>
                                <span className="text-slate-400">FTD</span>{" "}
                                <span className="font-mono font-semibold text-slate-700">{s.ftd_count}</span>
                                <span className="text-slate-400"> · </span>
                                <span className="font-mono text-slate-600">{fmtINR(s.ftd_value)}</span>
                            </div>
                            <div>
                                <span className="text-slate-400">Repeat</span>{" "}
                                <span className="font-mono font-semibold text-slate-700">{s.repeat_count}</span>
                            </div>
                        </div>
                    </button>
                );
            })}
        </div>
    );
}
